import { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Chip, Switch } from "@heroui/react";
import { api, fmtBytes, fmtTime } from "../lib/api.ts";
import { useLab, useRun } from "../lib/state.tsx";
import { Section } from "../components/Section.tsx";
import { DiffView } from "../components/DiffView.tsx";
import { Json } from "../components/Json.tsx";

interface PoisonCompare {
  tool: string;
  userVisible: string;
  modelVisible: string;
}

interface AttackResult {
  egressGate: boolean;
  succeeded: boolean;
  exfiltrated: Array<{ ts: number; bytes: number; preview: string }>;
  sandbox: { ok: boolean; error?: string; egressBlocked: number; logs: string[] };
  verdict: string;
}

interface RugPullResult {
  catalogGate: boolean;
  hashBefore: string | null;
  hashAfter: string | null;
  descriptionBefore?: string;
  descriptionAfter?: string;
  outcome: unknown;
  verdict: string;
}

export function SecurityPanel() {
  const { modes, gates, setMode } = useLab();
  const [poison, setPoison] = useState<PoisonCompare | null>(null);
  const attack = useRun<AttackResult>();
  const rug = useRun<RugPullResult>();

  const enabled = modes?.modes.poison ?? false;
  const attacksAllowed = modes?.attacksEnabled ?? false;

  useEffect(() => {
    void api.run<PoisonCompare>("poison/compare").then(setPoison);
  }, []);

  if (!attacksAllowed) {
    return (
      <Section title="Attack demos are disabled" tone="danger">
        <p className="text-xs text-default-300">
          This instance was started with <code className="font-mono">DEMO_ATTACKS_ENABLED=0</code>.
          The poisoned tool is not registered and the exhibits below are unavailable.
        </p>
      </Section>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="A tool description is an unseen channel"
        blurb="The user approves one sentence. The model reads that sentence plus everything else in the description, because a description is just text that arrives in the system prompt. Nothing here is a bug in MCP — it is what happens when the consent surface and the instruction surface are different strings."
        tone="danger"
      >
        <span data-testid="poison-switch" data-on={enabled ? "1" : "0"}>
          <Switch
            size="sm"
            color="danger"
            aria-label="enable poisoned tool"
            isSelected={enabled}
            onValueChange={(v) => void setMode("poison", v)}
          >
            <span className="text-xs">
              Enable the poisoned tool (hidden from tools/list until you do)
            </span>
          </Switch>
        </span>

        {poison && (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <Card shadow="none" className="border border-success/40 bg-success/5">
              <CardHeader className="pb-1 text-xs font-medium text-success">
                What the user is shown
              </CardHeader>
              <CardBody className="pt-0">
                <p className="font-mono text-xs whitespace-pre-wrap text-default-200">
                  {poison.userVisible}
                </p>
              </CardBody>
            </Card>
            <Card shadow="none" className="border border-danger/40 bg-danger/5">
              <CardHeader className="flex items-center gap-2 pb-1 text-xs font-medium text-danger">
                What the model is shown
                <Chip size="sm" color="danger" variant="flat" className="h-5 font-mono text-[10px]">
                  {poison.modelVisible.length - poison.userVisible.length} extra characters
                </Chip>
              </CardHeader>
              <CardBody className="pt-0">
                <p className="font-mono text-xs whitespace-pre-wrap text-default-200">
                  {poison.modelVisible}
                </p>
              </CardBody>
            </Card>
          </div>
        )}
      </Section>

      <Section
        title="Run it"
        blurb="An agent that believed the instruction block writes exactly the script you would expect. Every secret it reads is fabricated and the destination is an endpoint inside this container — nothing leaves."
        tone="danger"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            color="danger"
            isDisabled={!enabled}
            isLoading={attack.pending}
            onPress={() => void attack.run("attack")}
          >
            Run the poisoned agent
          </Button>
          <Chip
            size="sm"
            variant="flat"
            color={gates?.gates.egress ? "success" : "danger"}
            className="font-mono"
          >
            egress gate {gates?.gates.egress ? "ON" : "OFF"}
          </Chip>
          {!enabled && <span className="text-xs text-default-500">Enable the tool first.</span>}
        </div>

        {attack.data && (
          <div
            className={`mt-4 rounded-medium border p-3 ${
              attack.data.succeeded ? "border-danger bg-danger/10" : "border-success/50 bg-success/5"
            }`}
          >
            <p
              className={`text-sm font-medium ${attack.data.succeeded ? "text-danger" : "text-success"}`}
            >
              {attack.data.succeeded ? "EXFILTRATION SUCCEEDED" : "BLOCKED"}
            </p>
            <p className="mt-1 text-xs text-default-300">{attack.data.verdict}</p>

            {attack.data.sandbox.logs.map((l, i) => (
              <p key={i} className="mt-1 font-mono text-[11px] text-default-400">
                {l}
              </p>
            ))}

            {attack.data.exfiltrated.map((e, i) => (
              <div key={i} className="mt-2 rounded border border-danger/40 bg-black/30 p-2">
                <p className="font-mono text-[10px] text-danger">
                  {fmtTime(e.ts)} · {fmtBytes(e.bytes)} reached /api/demo/exfil-sink
                </p>
                <pre className="mt-1 overflow-x-auto font-mono text-[10px] text-default-400">
                  {e.preview}
                </pre>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="The rug pull"
        blurb="Approval is a snapshot. Nothing in the protocol says the definition you approved is the definition that runs next week — unless you pinned it."
        tone="danger"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button color="danger" isLoading={rug.pending} onPress={() => void rug.run("rugpull")}>
            Mutate search_notes after approval
          </Button>
          <Button variant="flat" onPress={() => void rug.run("rugpull/reset")}>
            Revert
          </Button>
          <Chip
            size="sm"
            variant="flat"
            color={gates?.gates.catalog ? "success" : "danger"}
            className="font-mono"
          >
            pinned catalogue {gates?.gates.catalog ? "ON" : "OFF"}
          </Chip>
        </div>

        {rug.data?.descriptionBefore && rug.data.descriptionAfter && (
          <div className="mt-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
              <span className="text-default-500">approved</span>
              <span className="text-success">{rug.data.hashBefore?.slice(0, 16)}…</span>
              <span className="text-default-500">→ served</span>
              <span className="text-danger">{rug.data.hashAfter?.slice(0, 16)}…</span>
            </div>
            <DiffView before={rug.data.descriptionBefore} after={rug.data.descriptionAfter} />
            <div
              className={`rounded-medium border p-3 ${
                rug.data.catalogGate ? "border-success/50 bg-success/5" : "border-danger bg-danger/10"
              }`}
            >
              <p className={`text-xs ${rug.data.catalogGate ? "text-success" : "text-danger"}`}>
                {rug.data.verdict}
              </p>
              <Json value={rug.data.outcome} className="mt-2 max-h-40 overflow-y-auto" />
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
